/**
 * Portrait - Reusable component for displaying character portraits in dialogs
 * Displays a character portrait image with a border frame
 */

export interface PortraitConfig {
    portraitUrl: string;
    position?: "left" | "right"; // Position of portrait (default: left)
    size?: number; // Size of the portrait in pixels (default: 120)
}

export class Portrait {
    private scene: Phaser.Scene;
    private container: Phaser.GameObjects.Container;
    private portraitImage: Phaser.GameObjects.Image | null = null;
    private border: Phaser.GameObjects.Rectangle | null = null;
    private background: Phaser.GameObjects.Rectangle | null = null;

    private portraitUrl: string;
    private position: "left" | "right";
    private size: number;

    constructor(scene: Phaser.Scene, config: PortraitConfig) {
        this.scene = scene;
        this.portraitUrl = config.portraitUrl;
        this.position = config.position || "left";
        this.size = config.size || 120;

        this.container = scene.add.container(0, 0);
    }

    /**
     * Draw the portrait at the specified position
     * @param dialogBoxY - Y position of the dialog box (to align portrait with it)
     */
    draw(dialogBoxY: number) {
        // Clear existing elements
        this.container.removeAll(true);
        this.portraitImage = null;
        this.border = null;
        this.background = null;

        // Calculate position based on left/right alignment
        const margin = 20;
        let x: number;

        if (this.position === "left") {
            x = margin;
        } else {
            x = this.scene.scale.width - this.size - margin;
        }

        const y = dialogBoxY - this.size / 2; // Center vertically with dialog box top

        // Background
        this.background = this.scene.add.rectangle(x, y, this.size, this.size, 0x000000, 0.9);
        this.background.setOrigin(0, 0);
        this.container.add(this.background);

        // Load and display portrait image
        const portraitKey = `portrait_${this.portraitUrl}`;

        // Check if texture is already loaded
        if (this.scene.textures.exists(portraitKey)) {
            this.createPortraitImage(portraitKey, x, y);
        } else {
            // Load the texture
            this.scene.load.image(portraitKey, this.portraitUrl);
            this.scene.load.once("complete", () => {
                this.createPortraitImage(portraitKey, x, y);
            });
            this.scene.load.start();
        }

        // Border
        this.border = this.scene.add.rectangle(x, y, this.size, this.size, 0xffffff, 0);
        this.border.setOrigin(0, 0);
        this.border.setStrokeStyle(3, 0xffcc00);
        this.container.add(this.border);

        this.container.setDepth(999); // Just below dialog box (depth 1000)
    }

    private createPortraitImage(textureKey: string, x: number, y: number) {
        if (!this.scene.textures.exists(textureKey)) {
            console.warn(`Portrait texture ${textureKey} not found`);
            return;
        }

        this.portraitImage = this.scene.add.image(x + this.size / 2, y + this.size / 2, textureKey);

        // Scale to fit within the portrait frame
        const scaleX = this.size / this.portraitImage.width;
        const scaleY = this.size / this.portraitImage.height;
        const scale = Math.min(scaleX, scaleY);
        this.portraitImage.setScale(scale);

        // Add to container (insert before border)
        if (this.container.list.length >= 2) {
            this.container.addAt(this.portraitImage, 1);
        } else {
            this.container.add(this.portraitImage);
        }
    }

    destroy() {
        this.container.destroy();
    }
}
